import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';

import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { CourtSplitterService } from '../services/CourtSplitterService';
import { BalanceCalculator } from '../services/BalanceCalculator';
import { ExportService } from '../services/ExportService';
import { CourtSplitterSync } from '../services/CourtSplitterSync';
import {
  CourtRoom,
  CourtSession,
  CourtSplitterProps,
  MemberBalance,
  Settlement,
  Currency,
  CURRENCY_SYMBOLS,
  SessionInput,
  PaymentInput,
} from '../types';
import './CourtSplitter.css';

type View = 'room-list' | 'room-view' | 'add-session' | 'edit-session';

const CourtSplitterInner: React.FC<CourtSplitterProps> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  const [service] = useState(() => new CourtSplitterService());
  const [sync] = useState(() => new CourtSplitterSync());
  const [currentView, setCurrentView] = useState<View>('room-list');
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);

  // Form state
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomCurrency, setNewRoomCurrency] = useState<Currency>('USD');
  const [newMemberName, setNewMemberName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [sessionDate, setSessionDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [sessionCost, setSessionCost] = useState('');
  const [sessionPaidBy, setSessionPaidBy] = useState('');
  const [sessionAttendees, setSessionAttendees] = useState<string[]>([]);
  const [sessionNote, setSessionNote] = useState('');

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  // User identity
  const userId = user?.id || 'anonymous';
  const userName = user?.name && user.name !== 'Google User' && user.name !== 'Guest'
    ? user.name
    : user?.email
      ? user.email.split('@')[0]
      : 'You';

  // Configure service with user identity
  useEffect(() => {
    if (isAuthenticated && user) {
      service.setUser(userId, userName);
      sync.setUserId(userId);
    }
  }, [isAuthenticated, user, userId, userName, service, sync]);

  // Derived data
  const rooms = useMemo(() => service.getRoomsSync(), [service, refreshKey]);
  const selectedRoom = useMemo(
    () => (selectedRoomId ? service.getRoomSync(selectedRoomId) : null),
    [service, selectedRoomId, refreshKey]
  );
  const balances = useMemo(
    () => (selectedRoom ? BalanceCalculator.calculateBalances(selectedRoom) : []),
    [selectedRoom]
  );
  const settlements = useMemo(
    () => (selectedRoom ? BalanceCalculator.calculateSettlements(selectedRoom) : []),
    [selectedRoom]
  );
  const summaryStats = useMemo(
    () => (selectedRoom ? BalanceCalculator.getSummaryStats(selectedRoom) : null),
    [selectedRoom]
  );

  // --- Deep link: auto-join via ?roomCode=XXXXXX ---
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const roomCode = params.get('roomCode');
    if (roomCode) {
      const room = service.findRoomByCode(roomCode);
      if (room) {
        service.joinRoom(room.id);
        setSelectedRoomId(room.id);
        setCurrentView('room-view');
        refresh();
        addNotification({ type: NotificationType.SUCCESS, title: 'Joined Room', message: `Joined "${room.name}"!`, duration: 3000 });
      } else {
        addNotification({ type: NotificationType.ERROR, title: 'Room Not Found', message: `No room found with code "${roomCode.toUpperCase()}".`, duration: 4000 });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('roomCode');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // --- Real-time sync ---
  useEffect(() => {
    if (!selectedRoomId || !isAuthenticated) return;
    sync.subscribe(selectedRoomId, {
      onSessionAdded: (session) => {
        refresh();
        addNotification({ type: NotificationType.INFO, title: 'New Session', message: `${session.date} session added by another member.`, duration: 3000 });
      },
      onSessionDeleted: () => refresh(),
      onSessionEdited: () => refresh(),
      onMemberJoined: (member) => {
        refresh();
        addNotification({ type: NotificationType.INFO, title: 'Member Joined', message: `${member.name} joined the room.`, duration: 3000 });
      },
      onMemberRemoved: () => refresh(),
      onPaymentRecorded: () => refresh(),
    });
    return () => { sync.unsubscribe(selectedRoomId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoomId, isAuthenticated]);

  // --- Auth gate ---
  if (!isAuthenticated) {
    return (
      <div className="court-splitter court-splitter--login-required">
        <div className="court-splitter__login-prompt">
          <span className="court-splitter__login-icon">🏸</span>
          <h2>Court Splitter</h2>
          <p>Please log in to track court payments with your friends.</p>
          {onExit && (
            <button className="court-splitter__btn court-splitter__btn--secondary" onClick={onExit}>
              Back to Hub
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- Handlers ---
  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      addNotification({ type: NotificationType.WARNING, title: 'Name required', message: 'Please enter a room name.', duration: 3000 });
      return;
    }
    await service.createRoom({ name: newRoomName.trim(), currency: newRoomCurrency });
    setNewRoomName('');
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Room Created', message: `"${newRoomName.trim()}" is ready!`, duration: 3000 });
  };

  const handleJoinByCode = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      addNotification({ type: NotificationType.WARNING, title: 'Invalid Code', message: 'Room codes are 6 characters.', duration: 3000 });
      return;
    }
    const room = service.findRoomByCode(code);
    if (!room) {
      addNotification({ type: NotificationType.ERROR, title: 'Room Not Found', message: `No room found with code "${code}".`, duration: 4000 });
      return;
    }
    await service.joinRoom(room.id);
    setJoinCode('');
    setSelectedRoomId(room.id);
    setCurrentView('room-view');
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Joined Room', message: `Joined "${room.name}"!`, duration: 3000 });
  };

  const handleShareRoom = (code: string) => {
    const url = `${window.location.origin}/court-splitter?roomCode=${code}`;
    navigator.clipboard.writeText(url).then(() => {
      addNotification({ type: NotificationType.SUCCESS, title: 'Link Copied!', message: 'Share link copied to clipboard.', duration: 2000 });
    }).catch(() => {
      addNotification({ type: NotificationType.ERROR, title: 'Copy Failed', message: 'Could not copy to clipboard.', duration: 3000 });
    });
  };

  const handleSelectRoom = (roomId: string) => {
    setSelectedRoomId(roomId);
    setCurrentView('room-view');
    refresh();
  };

  const handleDeleteRoom = async (roomId: string) => {
    await service.deleteRoom(roomId);
    if (selectedRoomId === roomId) {
      setSelectedRoomId(null);
      setCurrentView('room-list');
    }
    refresh();
    addNotification({ type: NotificationType.INFO, title: 'Room Deleted', message: 'Room has been removed.', duration: 3000 });
  };

  const handleAddMember = async () => {
    if (!selectedRoomId || !newMemberName.trim()) {
      addNotification({ type: NotificationType.WARNING, title: 'Name required', message: 'Please enter a member name.', duration: 3000 });
      return;
    }
    await service.addMember(selectedRoomId, newMemberName.trim());
    setNewMemberName('');
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Member Added', message: `${newMemberName.trim()} joined the room.`, duration: 3000 });
  };

  const handleOpenAddSession = () => {
    setEditingSessionId(null);
    setSessionDate(new Date().toISOString().split('T')[0]);
    setSessionCost('');
    setSessionPaidBy(selectedRoom?.members[0]?.id || '');
    setSessionAttendees(selectedRoom?.members.map(m => m.id) || []);
    setSessionNote('');
    setCurrentView('add-session');
  };

  const handleOpenEditSession = (session: CourtSession) => {
    setEditingSessionId(session.id);
    setSessionDate(session.date);
    setSessionCost(session.totalCost.toString());
    setSessionPaidBy(session.paidBy);
    setSessionAttendees([...session.attendees]);
    setSessionNote(session.note || '');
    setCurrentView('edit-session');
  };

  const handleAddSession = async () => {
    if (!selectedRoomId) return;
    const cost = parseFloat(sessionCost);
    if (isNaN(cost) || cost <= 0) {
      addNotification({ type: NotificationType.WARNING, title: 'Invalid cost', message: 'Please enter a valid cost amount.', duration: 3000 });
      return;
    }
    if (!sessionPaidBy) {
      addNotification({ type: NotificationType.WARNING, title: 'Payer required', message: 'Please select who paid.', duration: 3000 });
      return;
    }
    if (sessionAttendees.length === 0) {
      addNotification({ type: NotificationType.WARNING, title: 'Attendees required', message: 'Please select at least one attendee.', duration: 3000 });
      return;
    }

    const input: SessionInput = {
      date: sessionDate,
      totalCost: cost,
      paidBy: sessionPaidBy,
      attendees: sessionAttendees,
      note: sessionNote.trim() || undefined,
    };

    await service.addSession(selectedRoomId, input);
    setCurrentView('room-view');
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Session Added', message: `Court session on ${sessionDate} recorded.`, duration: 3000 });
  };

  const handleEditSession = async () => {
    if (!selectedRoomId || !editingSessionId) return;
    const cost = parseFloat(sessionCost);
    if (isNaN(cost) || cost <= 0) {
      addNotification({ type: NotificationType.WARNING, title: 'Invalid cost', message: 'Please enter a valid cost amount.', duration: 3000 });
      return;
    }

    await service.editSession(selectedRoomId, editingSessionId, {
      date: sessionDate,
      totalCost: cost,
      paidBy: sessionPaidBy,
      attendees: sessionAttendees,
      note: sessionNote.trim() || undefined,
    });
    setCurrentView('room-view');
    setEditingSessionId(null);
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Session Updated', message: 'Session has been updated.', duration: 3000 });
  };

  const handleRemoveSession = async (sessionId: string) => {
    if (!selectedRoomId) return;
    await service.deleteSession(selectedRoomId, sessionId);
    refresh();
    addNotification({ type: NotificationType.INFO, title: 'Session Removed', message: 'Session has been deleted.', duration: 3000 });
  };

  const handleMarkSettlementPaid = async (settlement: Settlement) => {
    if (!selectedRoomId) return;
    const input: PaymentInput = {
      fromMemberId: settlement.fromMemberId,
      toMemberId: settlement.toMemberId,
      amount: settlement.amount,
      date: new Date().toISOString().split('T')[0],
    };
    await service.recordPayment(selectedRoomId, input);
    refresh();
    addNotification({ type: NotificationType.SUCCESS, title: 'Payment Recorded', message: `${settlement.fromMemberName} → ${settlement.toMemberName} settled.`, duration: 3000 });
  };

  const handleExportCSV = () => {
    if (!selectedRoom) return;
    ExportService.downloadCSV(selectedRoom);
    addNotification({ type: NotificationType.SUCCESS, title: 'Exported', message: 'CSV file downloaded.', duration: 2000 });
  };

  const handleShareSummary = async () => {
    if (!selectedRoom) return;
    const success = await ExportService.copyBalanceSummary(selectedRoom);
    if (success) {
      addNotification({ type: NotificationType.SUCCESS, title: 'Copied!', message: 'Balance summary copied to clipboard.', duration: 2000 });
    } else {
      addNotification({ type: NotificationType.ERROR, title: 'Copy Failed', message: 'Could not copy to clipboard.', duration: 3000 });
    }
  };

  const toggleAttendee = (memberId: string) => {
    setSessionAttendees(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const handleLoadDemoData = () => {
    const room = service.loadDemoData();
    refresh();
    setSelectedRoomId(room.id);
    setCurrentView('room-view');
    addNotification({ type: NotificationType.SUCCESS, title: 'Demo Loaded', message: 'Loaded "Tuesday Badminton Crew" with 12 sessions.', duration: 3000 });
  };

  // --- Render helpers ---
  const currencySymbol = selectedRoom ? CURRENCY_SYMBOLS[selectedRoom.currency] : '$';

  const renderRoomList = () => (
    <div className="court-splitter__room-list">
      <div className="court-splitter__create-form">
        <input
          type="text"
          className="court-splitter__input"
          placeholder="New room name (e.g., Tuesday Badminton)"
          value={newRoomName}
          onChange={e => setNewRoomName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreateRoom()}
        />
        <select
          className="court-splitter__select court-splitter__select--currency"
          value={newRoomCurrency}
          onChange={e => setNewRoomCurrency(e.target.value as Currency)}
        >
          <option value="USD">$ USD</option>
          <option value="INR">₹ INR</option>
          <option value="EUR">€ EUR</option>
          <option value="GBP">£ GBP</option>
        </select>
        <button className="court-splitter__btn court-splitter__btn--primary" onClick={handleCreateRoom}>
          + Create Room
        </button>
      </div>

      <div className="court-splitter__join-form">
        <input
          type="text"
          className="court-splitter__input"
          placeholder="Enter room code (e.g., B4K9X2)"
          value={joinCode}
          maxLength={6}
          onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          onKeyDown={e => e.key === 'Enter' && handleJoinByCode()}
        />
        <button className="court-splitter__btn court-splitter__btn--primary" onClick={handleJoinByCode}>
          Join
        </button>
      </div>

      {rooms.length === 0 ? (
        <div className="court-splitter__empty">
          <span className="court-splitter__empty-icon">🏸</span>
          <p>No rooms yet. Create one to start tracking court payments!</p>
        </div>
      ) : (
        <div className="court-splitter__rooms">
          {rooms.map(room => (
            <div key={room.id} className="court-splitter__room-card" onClick={() => handleSelectRoom(room.id)}>
              <div className="court-splitter__room-card-info">
                <h3>{room.name}</h3>
                <span className="court-splitter__room-card-code">{room.id}</span>
                <span className="court-splitter__room-meta">
                  {room.members.length} members · {room.sessions.length} sessions · {CURRENCY_SYMBOLS[room.currency]}
                </span>
              </div>
              <button
                className="court-splitter__btn court-splitter__btn--danger court-splitter__btn--small"
                onClick={e => { e.stopPropagation(); handleDeleteRoom(room.id); }}
                title="Delete room"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

      <button className="court-splitter__demo-btn" onClick={handleLoadDemoData}>
        🧪 Load Demo Data
      </button>
    </div>
  );

  const renderSessionList = () => {
    if (!selectedRoom) return null;

    const sortedSessions = [...selectedRoom.sessions].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const totalCost = sortedSessions.reduce((sum, s) => sum + s.totalCost, 0);

    return (
      <div className="court-splitter__sessions">
        <div className="court-splitter__room-banner">
          <div className="court-splitter__room-banner-info">
            <span className="court-splitter__room-banner-label">Room Code:</span>
            <span className="court-splitter__room-banner-code">{selectedRoom.id}</span>
          </div>
          <div className="court-splitter__room-banner-actions">
            <button
              className="court-splitter__btn court-splitter__btn--small"
              onClick={() => {
                navigator.clipboard.writeText(selectedRoom.id);
                addNotification({ type: NotificationType.SUCCESS, title: 'Copied!', message: 'Room code copied.', duration: 2000 });
              }}
              title="Copy code"
            >
              📋 Copy
            </button>
            <button
              className="court-splitter__btn court-splitter__btn--small"
              onClick={() => handleShareRoom(selectedRoom.id)}
              title="Share invite link"
            >
              📤 Share
            </button>
          </div>
        </div>

        <div className="court-splitter__section-header">
          <h3>Sessions</h3>
          <button className="court-splitter__btn court-splitter__btn--primary court-splitter__btn--small" onClick={handleOpenAddSession}>
            + Add Session
          </button>
        </div>

        {selectedRoom.sessions.length === 0 ? (
          <p className="court-splitter__empty-text">No sessions yet. Add one after your next game!</p>
        ) : (
          <div className="court-splitter__table-wrapper">
            <table className="court-splitter__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Cost ({currencySymbol})</th>
                  <th>Paid By</th>
                  <th>Players</th>
                  <th>Per Person</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedSessions.map(session => {
                  const payer = selectedRoom.members.find(m => m.id === session.paidBy);
                  const attendeeNames = session.attendees
                    .map(id => selectedRoom.members.find(m => m.id === id)?.name || 'Unknown')
                    .join(', ');
                  const perPerson = session.attendees.length > 0
                    ? (session.totalCost / session.attendees.length).toFixed(2)
                    : '—';

                  return (
                    <tr key={session.id}>
                      <td>{session.date}</td>
                      <td>{currencySymbol}{session.totalCost.toFixed(2)}</td>
                      <td>{payer?.name || 'Unknown'}</td>
                      <td>{attendeeNames}</td>
                      <td>{currencySymbol}{perPerson}</td>
                      <td className="court-splitter__table-note">{session.note || '—'}</td>
                      <td className="court-splitter__table-actions">
                        <button
                          className="court-splitter__btn court-splitter__btn--ghost court-splitter__btn--small"
                          onClick={() => handleOpenEditSession(session)}
                          title="Edit session"
                        >
                          ✏️
                        </button>
                        <button
                          className="court-splitter__btn court-splitter__btn--danger court-splitter__btn--small"
                          onClick={() => handleRemoveSession(session.id)}
                          title="Remove session"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="court-splitter__table-total">
                  <td>{sortedSessions.length} sessions</td>
                  <td>{currencySymbol}{totalCost.toFixed(2)}</td>
                  <td colSpan={5}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderBalances = () => (
    <div className="court-splitter__balances">
      <h4>Balances</h4>
      {balances.length === 0 ? (
        <p className="court-splitter__empty-text">No data yet.</p>
      ) : (
        <div className="court-splitter__balance-list">
          {balances.map(b => (
            <div key={b.memberId} className="court-splitter__balance-item">
              <span className="court-splitter__balance-name">{b.memberName}</span>
              <span className={`court-splitter__balance-amount ${b.netBalance > 0 ? 'positive' : b.netBalance < 0 ? 'negative' : ''}`}>
                {b.netBalance >= 0 ? '+' : ''}{currencySymbol}{Math.abs(b.netBalance).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {settlements.length > 0 && (
        <div className="court-splitter__settlements">
          <h4>Settle Up</h4>
          {settlements.map((s, i) => (
            <div key={i} className="court-splitter__settlement-item">
              <span>{s.fromMemberName}</span>
              <span className="court-splitter__settlement-arrow">→ {currencySymbol}{s.amount.toFixed(2)} →</span>
              <span>{s.toMemberName}</span>
              <button
                className="court-splitter__btn court-splitter__btn--small court-splitter__btn--accent"
                onClick={() => handleMarkSettlementPaid(s)}
                title="Mark as paid"
              >
                ✓ Paid
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStats = () => {
    if (balances.length === 0) {
      return (
        <div className="court-splitter__stats">
          <p className="court-splitter__empty-text">No payment data yet.</p>
        </div>
      );
    }

    const sortedBalances = [...balances].sort((a, b) => b.totalPaid - a.totalPaid);
    const maxPaid = Math.max(...sortedBalances.map(b => b.totalPaid), 1);
    const sessionsPaidCount = BalanceCalculator.getSessionsPaidCount(selectedRoom?.sessions || []);

    return (
      <div className="court-splitter__stats">
        {summaryStats && (
          <div className="court-splitter__summary-stats">
            <span>{summaryStats.totalSessions} sessions</span>
            <span>{currencySymbol}{summaryStats.totalSpent.toFixed(2)} total</span>
            <span>{currencySymbol}{summaryStats.averagePerPerson.toFixed(2)}/person</span>
          </div>
        )}
        <div className="court-splitter__chart">
          {sortedBalances.map(b => {
            const widthPercent = maxPaid > 0 ? (b.totalPaid / maxPaid) * 100 : 0;
            const barClass = b.netBalance > 0.01
              ? 'court-splitter__chart-bar--positive'
              : b.netBalance < -0.01
                ? 'court-splitter__chart-bar--negative'
                : 'court-splitter__chart-bar--neutral';
            const sessionsCount = sessionsPaidCount.get(b.memberId) || 0;

            return (
              <div key={b.memberId} className="court-splitter__chart-row">
                <div className="court-splitter__chart-label">
                  <span>{b.memberName}</span>
                  <span className="court-splitter__chart-label-detail">
                    {currencySymbol}{b.totalPaid.toFixed(2)} · {sessionsCount} session{sessionsCount !== 1 ? 's' : ''} paid
                  </span>
                </div>
                <div className="court-splitter__chart-bar-wrapper">
                  <div
                    className={`court-splitter__chart-bar ${barClass}`}
                    style={{ width: `${Math.max(widthPercent, 5)}%` }}
                  >
                    {currencySymbol}{b.totalPaid.toFixed(0)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMembers = () => {
    if (!selectedRoom) return null;

    return (
      <div className="court-splitter__members">
        <h4>Members ({selectedRoom.members.length})</h4>
        <div className="court-splitter__member-list">
          {selectedRoom.members.map(m => (
            <div key={m.id} className="court-splitter__member-item">
              <span>{m.name} {m.isAdmin ? '👑' : ''}</span>
              {!m.isAdmin && selectedRoom.createdBy === userId && (
                <button
                  className="court-splitter__btn court-splitter__btn--danger court-splitter__btn--small"
                  onClick={() => service.removeMember(selectedRoom.id, m.id).then(refresh)}
                  title="Remove member"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="court-splitter__add-member">
          <input
            type="text"
            className="court-splitter__input court-splitter__input--small"
            placeholder="Add member name"
            value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddMember()}
          />
          <button className="court-splitter__btn court-splitter__btn--primary court-splitter__btn--small" onClick={handleAddMember}>
            +
          </button>
        </div>
      </div>
    );
  };

  const renderSessionForm = () => {
    if (!selectedRoom) return null;
    const isEditing = currentView === 'edit-session';

    return (
      <div className="court-splitter__add-session-form">
        <h3>{isEditing ? 'Edit Session' : 'Add Court Session'}</h3>

        <label className="court-splitter__label">
          Date
          <input
            type="date"
            className="court-splitter__input"
            value={sessionDate}
            onChange={e => setSessionDate(e.target.value)}
          />
        </label>

        <label className="court-splitter__label">
          Total Cost ({currencySymbol})
          <input
            type="number"
            className="court-splitter__input"
            placeholder="0.00"
            min="0"
            step="0.01"
            value={sessionCost}
            onChange={e => setSessionCost(e.target.value)}
          />
        </label>

        <label className="court-splitter__label">
          Paid By
          <select
            className="court-splitter__select"
            value={sessionPaidBy}
            onChange={e => setSessionPaidBy(e.target.value)}
          >
            <option value="">Select who paid</option>
            {selectedRoom.members.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>

        <fieldset className="court-splitter__fieldset">
          <legend>Who Played?</legend>
          <div className="court-splitter__attendee-checkboxes">
            {selectedRoom.members.map(m => (
              <label key={m.id} className="court-splitter__checkbox-label">
                <input
                  type="checkbox"
                  checked={sessionAttendees.includes(m.id)}
                  onChange={() => toggleAttendee(m.id)}
                />
                {m.name}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="court-splitter__label">
          Note (optional)
          <input
            type="text"
            className="court-splitter__input"
            placeholder="e.g., Court 3, 2 hours"
            value={sessionNote}
            onChange={e => setSessionNote(e.target.value)}
          />
        </label>

        <div className="court-splitter__form-actions">
          <button className="court-splitter__btn court-splitter__btn--secondary" onClick={() => setCurrentView('room-view')}>
            Cancel
          </button>
          <button
            className="court-splitter__btn court-splitter__btn--primary"
            onClick={isEditing ? handleEditSession : handleAddSession}
          >
            {isEditing ? 'Save Changes' : 'Save Session'}
          </button>
        </div>
      </div>
    );
  };

  // --- Layout ---

  if (currentView === 'room-list') {
    return (
      <div className="court-splitter">
        <div className="court-splitter__header">
          <h2>🏸 Court Splitter</h2>
          <p className="court-splitter__subtitle">Track badminton court payments with friends</p>
        </div>
        {renderRoomList()}
      </div>
    );
  }

  if (currentView === 'add-session' || currentView === 'edit-session') {
    return (
      <div className="court-splitter">
        <button className="court-splitter__btn court-splitter__btn--back" onClick={() => setCurrentView('room-view')}>
          ← Back to Room
        </button>
        {renderSessionForm()}
      </div>
    );
  }

  // Room view — use UniversalGameLayout
  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: selectedRoom?.name || 'Court Splitter',
    board: renderSessionList(),
    leftPanels: [
      {
        id: 'stats',
        title: 'Stats',
        icon: '📊',
        content: renderStats(),
      },
      {
        id: 'balances',
        title: 'Balances',
        icon: '💰',
        content: renderBalances(),
      },
      {
        id: 'members',
        title: 'Members',
        icon: '👥',
        content: renderMembers(),
      },
    ],
    actions: [
      {
        id: 'back-to-rooms',
        label: 'All Rooms',
        icon: '←',
        onClick: () => { setSelectedRoomId(null); setCurrentView('room-list'); },
      },
      {
        id: 'add-session',
        label: 'Add Session',
        icon: '+',
        onClick: handleOpenAddSession,
        variant: 'primary' as const,
      },
      {
        id: 'export-csv',
        label: 'Export CSV',
        icon: '📥',
        onClick: handleExportCSV,
        variant: 'secondary' as const,
      },
      {
        id: 'share-summary',
        label: 'Share Summary',
        icon: '📋',
        onClick: handleShareSummary,
        variant: 'secondary' as const,
      },
    ],
  };

  return (
    <div className="court-splitter">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

export const CourtSplitter: React.FC<CourtSplitterProps> = (props) => (
  <ErrorBoundary componentName="CourtSplitter">
    <CourtSplitterInner {...props} />
  </ErrorBoundary>
);

export default CourtSplitter;
